import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, skip, tap } from 'rxjs/operators';

import { AuthStore } from '@store/auth.store';
import { ADMIN_LANDING_PAGES } from '@enums/admin-permission.enum';
import { isUnsupportedByApi } from '@shared/utils/graphql-support.util';

const MY_ADMIN_PERMISSIONS = gql`
  query myAdminPermissions {
    myAdminPermissions {
      isSuperadmin
      permissions
    }
  }
`;

export interface AdminPermissionState {
  isSuperadmin: boolean;
  permissions: ReadonlySet<string>;
  /**
   * False when the API predates admin roles (or could not be asked). Everything
   * is shown then, as before roles existed — the API still refuses what it
   * refuses, so hiding pages on a guess would only lock admins out.
   */
  enforced: boolean;
}

const UNRESTRICTED: AdminPermissionState = {
  isSuperadmin: false,
  permissions: new Set(),
  enforced: false,
};

/**
 * The signed-in admin's permissions, fetched once per session.
 *
 * Only decides what the dashboard shows. Every call is checked again by the API,
 * which is the authority — so a stale answer here costs a refused click, never
 * access. It is refetched when the token changes (login, logout) and whenever
 * the API refuses a call for a missing permission, so a role changed by someone
 * else catches up on the next refusal.
 */
@Injectable({ providedIn: 'root' })
export class AdminPermissionService {
  private readonly state = new BehaviorSubject<AdminPermissionState | null>(null);
  private inflight: Observable<AdminPermissionState> | null = null;
  /** Bumped on reset, so a fetch started under an older token cannot land. */
  private generation = 0;

  /** Null until the first load finishes. */
  readonly state$ = this.state.asObservable();

  constructor(
    private apollo: Apollo,
    authStore: AuthStore,
  ) {
    authStore.token$
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe(() => this.reset());
  }

  get snapshot(): AdminPermissionState | null {
    return this.state.getValue();
  }

  load(): Observable<AdminPermissionState> {
    const current = this.state.getValue();
    if (current) return of(current);

    if (!this.inflight) {
      const generation = this.generation;
      const settle = (loaded: AdminPermissionState) => {
        if (generation === this.generation) this.state.next(loaded);
      };
      this.inflight = this.apollo
        .query<{ myAdminPermissions: { isSuperadmin: boolean; permissions: string[] } }>({
          query: MY_ADMIN_PERMISSIONS,
          fetchPolicy: 'network-only',
        })
        .pipe(
          map((res) => {
            const mine = res.data?.myAdminPermissions;
            if (!mine) throw new Error('myAdminPermissions returned no data');
            return {
              isSuperadmin: mine.isSuperadmin,
              permissions: new Set(mine.permissions ?? []),
              enforced: true,
            } as AdminPermissionState;
          }),
          tap(settle),
          catchError((error) => {
            // An older API has no roles to enforce. Any other failure also
            // falls back to showing everything — the API still refuses what it
            // refuses — rather than leaving an empty nav; it is retried on the
            // next login or the next refusal.
            if (!isUnsupportedByApi(error)) {
              console.warn('Could not load admin permissions; showing every page.', error);
            }
            settle(UNRESTRICTED);
            return of(UNRESTRICTED);
          }),
          finalize(() => {
            if (generation === this.generation) this.inflight = null;
          }),
          shareReplay(1),
        );
    }
    return this.inflight;
  }

  /** Drops the cached answer and fetches it again. */
  refresh(): void {
    this.reset();
    this.load().subscribe();
  }

  /** Whether the admin holds `permission`. False until permissions have loaded. */
  can(permission: string): boolean {
    const current = this.state.getValue();
    return !!current && AdminPermissionService.allows(current, permission);
  }

  static allows(state: AdminPermissionState, permission: string): boolean {
    return !state.enforced || state.isSuperadmin || state.permissions.has(permission);
  }

  /** The first page in navigation order the admin may open, if any. */
  static landingPage(state: AdminPermissionState): string | null {
    return (
      ADMIN_LANDING_PAGES.find((page) => AdminPermissionService.allows(state, page.permission))
        ?.path ?? null
    );
  }

  private reset(): void {
    this.generation++;
    this.inflight = null;
    this.state.next(null);
  }
}
